import { PurchaseOrder } from '../../../../clients/quipli-service/purchase-order/purchase-order';
import { Modal } from '../../../../components/modal/modal';
import { Button } from '../../../../components/form/button';
import { Spinner } from '../../../../components/spinner/spinner';
import { ProgressIndicator } from '../../../../components/progress-indicator/progress-indicator';
import { useReceivePurchaseOrderWorkflow } from '../../hooks/use-receive-purchase-order-workflow';
import { QuantityProductForm } from './quantity-product-form';
import { ProductUnitForm } from './product-unit-form';
import { ReceiveSummary } from './receive-summary';

export const ReceivePurchaseOrder = ({
  purchaseOrder,
  onCancel,
  onComplete,
}: {
  purchaseOrder: PurchaseOrder;
  onCancel: () => void;
  onComplete: () => void;
}) => {
  const {
    // Step management
    currentStep,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    isSummaryStep,
    steps,

    // Data
    currentStock,
    currentUnitUpdate,
    receivableProducts,
    stockUpdates,
    unitUpdates,

    // Validation
    isCurrentStepValid,
    canReceive,
    currentStepErrors,

    // Actions
    handleNext,
    handlePrevious,
    handleStockUpdate,
    handleUnitUpdate,
    handleReceive,

    // State
    isReceiving,

    // Form validation
    markFieldAsTouched,
    isFieldTouched,

    // Navigation
    onCancel: handleCancel,

    // UI helpers
    getStepTitle,
  } = useReceivePurchaseOrderWorkflow(purchaseOrder, onCancel, onComplete);

  return (
    <Modal
      isOpen={true}
      title={getStepTitle(currentStep)}
      actions={
        <>
          <Button
            dataCy="cancel-purchase-order-modal"
            className="button button-secondary"
            disabled={isReceiving}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          {!isFirstStep && (
            <Button dataCy="back-purchase-order-modal" className="button button-secondary" onClick={handlePrevious}>
              Back
            </Button>
          )}
          {!isLastStep && (
            <Button
              dataCy="next-purchase-order-modal"
              className="button button-yellow"
              disabled={!isCurrentStepValid}
              onClick={handleNext}
            >
              Next
            </Button>
          )}
          {isSummaryStep && (
            <Button
              dataCy="receive-purchase-order-modal"
              className="button button-yellow"
              disabled={isReceiving || !canReceive}
              onClick={handleReceive}
            >
              {isReceiving ? <Spinner /> : 'Receive'}
            </Button>
          )}
        </>
      }
      close={() => !isReceiving && handleCancel()}
    >
      <ProgressIndicator currentStep={currentStepIndex} totalSteps={steps.length} />

      {currentStep.type === 'quantity' && currentStock && (
        <QuantityProductForm
          purchaseOrderProduct={currentStep.product}
          stock={currentStock}
          onUpdate={handleStockUpdate}
          markFieldAsTouched={markFieldAsTouched}
          isFieldTouched={isFieldTouched}
          errors={currentStepErrors}
        />
      )}
      {currentStep.type === 'unit' && currentUnitUpdate && currentStep.unitIndex !== undefined && (
        <ProductUnitForm
          purchaseOrderProduct={currentStep.product}
          units={currentUnitUpdate.units}
          onUpdate={handleUnitUpdate}
          index={currentStep.unitIndex}
          markFieldAsTouched={markFieldAsTouched}
          isFieldTouched={isFieldTouched}
          errors={currentStepErrors}
        />
      )}
      {isSummaryStep && (
        <ReceiveSummary receivableProducts={receivableProducts} stocks={stockUpdates} units={unitUpdates} />
      )}
    </Modal>
  );
};
